/**
 * Task Card Component Module
 *
 * Pure computation functions for kanban task card rendering.
 *
 * @module components/taskCard
 */

/**
 * Calculate date urgency info for a task
 * @param {string|null} endDate - Task end date in ISO format
 * @param {string} status - Task status
 * @returns {Object} Urgency info with colors and styling
 */
export function calculateDateUrgency(endDate, status) {
    if (!endDate) {
        return {
            hasDate: false,
            urgency: 'none',
            bgColor: null,
            textColor: 'var(--text-muted)',
            borderColor: null,
            icon: '',
            iconColor: ''
        };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(endDate);
    dueDate.setHours(0, 0, 0, 0);
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Completed tasks: no urgency styling
    if (status === 'done') {
        return {
            hasDate: true,
            urgency: 'completed',
            diffDays,
            bgColor: 'rgba(148, 163, 184, 0.12)',
            textColor: '#94a3b8',
            borderColor: 'rgba(148, 163, 184, 0.25)',
            icon: '',
            iconColor: ''
        };
    }

    // Overdue - orange/yellow warning
    if (diffDays < 0) {
        return {
            hasDate: true,
            urgency: 'overdue',
            diffDays,
            bgColor: 'rgba(249, 115, 22, 0.2)',
            textColor: '#fb923c',
            borderColor: 'rgba(249, 115, 22, 0.4)',
            icon: '⚠ ',
            iconColor: '#f97316'
        };
    }

    // Within 1 week - purple/violet
    if (diffDays <= 7) {
        return {
            hasDate: true,
            urgency: 'soon',
            diffDays,
            bgColor: 'rgba(192, 132, 252, 0.25)',
            textColor: '#c084fc',
            borderColor: 'rgba(192, 132, 252, 0.5)',
            icon: '',
            iconColor: ''
        };
    }

    // Normal - blue glassmorphic
    return {
        hasDate: true,
        urgency: 'normal',
        diffDays,
        bgColor: 'rgba(59, 130, 246, 0.15)',
        textColor: '#93c5fd',
        borderColor: 'rgba(59, 130, 246, 0.3)',
        icon: '',
        iconColor: ''
    };
}

/**
 * Build the urgency-colored due-date chip for other surfaces (e.g. the
 * dashboard) so they render the same chip as the kanban cards.
 * @param {string|null} endDate - Task end date in ISO format
 * @param {string} status - Task status
 * @param {Object} helpers - { escapeHtml, formatDate }
 * @returns {string} HTML string for the date chip ('' when there is no date)
 */
export function generateDateChipHTML(endDate, status, helpers = {}) {
    // Same tinted, theme-aware chip as the kanban cards (readable in light and dark)
    return generateKanbanDueChipHTML(endDate, status, {
        escapeHtml: helpers.escapeHtml,
        formatDate: helpers.formatDate
    });
}

/**
 * Build the class-based due-date chip used on kanban cards. Colors come from
 * theme variables in style.css (.kanban-due-chip.is-*), so the chip follows
 * light/dark mode instantly. Overdue chips spell the state out in words.
 * When the task also has a start date the chip shows the range "start → end".
 * @param {string|null} endDate - Task end date in ISO format
 * @param {string} status - Task status
 * @param {Object} helpers - { escapeHtml, formatDate, overdueText, startDate }
 * @returns {string} HTML string for the chip ('' when there is no date)
 */
export function generateKanbanDueChipHTML(endDate, status, helpers = {}) {
    const escapeHtml = helpers.escapeHtml || ((s) => s);
    const formatDate = helpers.formatDate || ((d) => d);
    const urgency = calculateDateUrgency(endDate, status);
    if (!urgency.hasDate) return '';
    const variant = urgency.urgency === 'completed' ? 'done' : urgency.urgency;
    const endText = escapeHtml(formatDate(endDate));
    const startDate = helpers.startDate;
    const dateText = startDate && startDate !== endDate
        ? `${escapeHtml(formatDate(startDate))} → ${endText}`
        : endText;
    const label = variant === 'overdue' && helpers.overdueText
        ? `${escapeHtml(helpers.overdueText)} · ${endText}`
        : dateText;
    return `<span class="kanban-due-chip is-${variant}">${KANBAN_CALENDAR_ICON}${label}</span>`;
}

const KANBAN_CALENDAR_ICON = '<svg class="kanban-due-icon" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><rect x="2.5" y="3.5" width="11" height="10" rx="2"/><path d="M2.5 6.5h11M5.5 2v3M10.5 2v3"/></svg>';

/**
 * Generate HTML for a single kanban task card.
 * Layout: title first, project tag directly below (full name, wraps instead of
 * truncating), optional tags, then a footer with priority and due date.
 * @param {Object} task - Task object
 * @param {Object} helpers - Helper functions and data
 * @param {Function} helpers.escapeHtml - HTML escape function
 * @param {Function} helpers.formatDate - Date format function
 * @param {Function} helpers.getProjectColor - Get project color function
 * @param {Function} helpers.getTagColor - Get tag color function
 * @param {Function} helpers.getPriorityLabel - Get priority label function
 * @param {Array} helpers.projects - Array of projects
 * @param {Set} helpers.selectedCards - Set of selected card IDs
 * @param {boolean} helpers.showProjects - Show projects on cards
 * @param {boolean} helpers.showTags - Show tags on cards
 * @param {boolean} helpers.showNoDate - Show "No date" text
 * @param {string} helpers.noProjectText - Text for "No project"
 * @param {string} helpers.noDateText - Text for "No date"
 * @param {string} helpers.overdueText - Text for "Overdue" on the due chip
 * @returns {string} HTML string for task card
 */
export function generateTaskCardHTML(task, helpers) {
    const {
        escapeHtml,
        formatDate,
        getProjectColor,
        getTagColor,
        getPriorityLabel,
        projects,
        selectedCards,
        showProjects = true,
        showTags = true,
        showNoDate = true,
        automationLabel = 'Generated by automation',
        noProjectText,
        noDateText,
        overdueText = ''
    } = helpers;

    const proj = projects.find(p => p.id === task.projectId);

    let dueHTML = generateKanbanDueChipHTML(task.endDate, task.status, { escapeHtml, formatDate, overdueText, startDate: task.startDate });
    if (!dueHTML && showNoDate) {
        dueHTML = `<span class="kanban-due-chip is-none">${KANBAN_CALENDAR_ICON}${escapeHtml(noDateText || '')}</span>`;
    }

    const isSelected = selectedCards && selectedCards.has(task.id);
    const selectedClass = isSelected ? ' selected' : '';
    const doneClass = task.status === 'done' ? ' is-done' : '';
    const projectStyle = proj ? ` style="--project-color: ${getProjectColor(proj.id)};"` : '';

    let projectHTML = '';
    if (showProjects) {
        projectHTML = proj
            ? `<span class="kanban-project-tag" title="${escapeHtml(proj.name)}"><span class="kanban-project-swatch" aria-hidden="true"></span><span class="kanban-project-name">${escapeHtml(proj.name)}</span></span>`
            : `<span class="kanban-project-tag is-empty"><span class="kanban-project-name">${escapeHtml(noProjectText || '')}</span></span>`;
    }

    const tagsHtml = showTags && task.tags && task.tags.length > 0
        ? `<div class="kanban-card-tags">${task.tags.map(tag => {
            return `<span class="kanban-card-tag" style="--tag-color: ${getTagColor(tag)};">${escapeHtml(tag)}</span>`;
        }).join('')}</div>`
        : '';

    const priority = task.priority || '';
    const priorityHTML = priority
        ? `<span class="kanban-priority priority-${priority}"><span class="kanban-priority-dot" aria-hidden="true"></span>${escapeHtml(getPriorityLabel(priority))}</span>`
        : '';

    const automationBadge = task.automationId != null
        ? `<span class="task-automation-badge" title="${escapeHtml(automationLabel)}">🔃</span>`
        : '';

    // Compact layout: title + priority share a row; tags + due date share a row.
    const metaHTML = tagsHtml || dueHTML
        ? `<div class="kanban-card-meta">${tagsHtml || '<span></span>'}${dueHTML}</div>`
        : '';

    return `
        <div class="task-card kanban-card${selectedClass}${doneClass}" draggable="true" data-task-id="${task.id}"${projectStyle}>
            <div class="kanban-card-head">
                <div class="task-title">${automationBadge}<span>${escapeHtml(task.title || "")}</span></div>
                ${priorityHTML}
            </div>
            ${projectHTML}
            ${metaHTML}
        </div>
    `;
}
